import React from 'react';
import { DataTable, ColumnDef } from '@/components/data-display/DataTable';
import { PipelineRecord } from '@/types/domain/pipeline';
import { Button } from '@/components/ui/Button';
import { Eye, Clock, AlertTriangle, RefreshCw, CheckCircle2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/constants/routes';

interface PipelineTableViewProps {
  records: PipelineRecord[];
  isLoading: boolean;
  onRetrySapSync?: (id: string) => void;
  onClearFilters?: () => void;
}

const formatInr = (val: number) =>
  `₹${Number(val || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export const PipelineTableView: React.FC<PipelineTableViewProps> = ({
  records,
  isLoading,
  onRetrySapSync,
  onClearFilters,
}) => {
  const navigate = useNavigate();

  const columns: ColumnDef<PipelineRecord>[] = [
    {
      header: 'Request & ERP Trackers',
      accessorKey: 'requestNumber',
      cell: (row) => (
        <div className="space-y-1 font-poppins">
          <span className="font-extrabold text-primary text-xs block hover:underline cursor-pointer">
            {row.requestNumber}
          </span>
          <div className="flex flex-wrap gap-1 text-[9.5px] font-mono">
            {row.connectedIds.sapPrNumber && (
              <span className="bg-blue-500/10 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded border border-blue-500/20">
                PR: {row.connectedIds.sapPrNumber}
              </span>
            )}
            {row.connectedIds.poNumber && (
              <span className="bg-purple-500/10 text-purple-700 dark:text-purple-300 px-1.5 py-0.5 rounded border border-purple-500/20">
                PO: {row.connectedIds.poNumber}
              </span>
            )}
            {row.connectedIds.invoiceNumber && (
              <span className="bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 px-1.5 py-0.5 rounded border border-emerald-500/20">
                INV: {row.connectedIds.invoiceNumber}
              </span>
            )}
          </div>
        </div>
      ),
    },
    {
      header: 'Title & Category',
      accessorKey: 'title',
      cell: (row) => (
        <div className="max-w-xs space-y-1 font-poppins">
          <span className="font-bold text-foreground text-xs block truncate" title={row.title || 'Procurement Requisition'}>
            {row.title || 'Procurement Requisition'}
          </span>
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] font-bold uppercase text-foreground-muted bg-muted px-1.5 py-0.5 rounded">
              {row.category || 'CapitalEquipment'}
            </span>
            <span className="text-[10px] font-extrabold uppercase px-1.5 py-0.5 rounded border border-primary/20 bg-primary/10 text-primary">
              {row.capexOpex || 'OPEX'}
            </span>
          </div>
        </div>
      ),
    },
    {
      header: 'Requester & Dept',
      accessorKey: 'requesterName',
      cell: (row) => (
        <div className="text-xs space-y-0.5 font-poppins">
          <span className="font-bold text-foreground block">{row.requesterName || 'Sarah Jenkins'}</span>
          <span className="text-[11px] text-foreground-secondary block">{row.departmentName || 'Operations'}</span>
        </div>
      ),
    },
    {
      header: 'Committed Value',
      accessorKey: 'totalAmount',
      cell: (row) => (
        <span className="font-poppins font-bold text-xs text-foreground font-mono">
          {formatInr(row.totalAmount)}
        </span>
      ),
    },
    {
      header: 'Lifecycle Stage',
      accessorKey: 'currentStage',
      cell: (row) => {
        const stage = row.currentStage;
        let badgeColor = 'bg-primary/10 text-primary border-primary/20';
        if (stage === 'REQUEST' || stage === 'APPROVAL') badgeColor = 'bg-purple-500/10 text-purple-700 dark:text-purple-300 border-purple-500/20';
        else if (stage === 'SAP_PR') badgeColor = 'bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/20';
        else if (stage === 'RFQ' || stage === 'QUOTATION' || stage === 'SUPPLIER') badgeColor = 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/20';
        else if (stage === 'PO' || stage === 'DELIVERY' || stage === 'GRN') badgeColor = 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/20';
        else if (stage === 'INVOICE' || stage === 'PAYMENT' || stage === 'CLOSED') badgeColor = 'bg-stone-500/10 text-stone-700 dark:text-stone-300 border-stone-500/20';

        return (
          <div className="text-xs space-y-1 font-poppins">
            <span className={`font-bold px-2 py-0.5 rounded text-[11px] block w-fit border ${badgeColor}`}>
              {stage.replace('_', ' ')}
            </span>
            <span className="text-[10px] text-foreground-muted block font-medium truncate">
              {row.currentStatus}
            </span>
          </div>
        );
      },
    },
    {
      header: 'Exceptions / Delay',
      accessorKey: 'isDelayed',
      cell: (row) => (
        <div className="text-xs space-y-1 font-poppins">
          {row.isBlocked ? (
            <span className="inline-flex items-center gap-1 bg-rose-500/10 text-rose-700 dark:text-rose-300 border border-rose-500/20 px-2 py-0.5 rounded text-[10px] font-bold">
              <AlertTriangle className="h-3 w-3" /> Blocked
            </span>
          ) : row.isDelayed ? (
            <span className="inline-flex items-center gap-1 bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-500/20 px-2 py-0.5 rounded text-[10px] font-bold">
              <Clock className="h-3 w-3" /> Delayed ({row.delayDays}d)
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-bold text-[10px]">
              <CheckCircle2 className="h-3 w-3" /> On Schedule
            </span>
          )}
        </div>
      ),
    },
    {
      header: 'Action Workspace',
      cell: (row) => (
        <div className="flex items-center gap-1.5 font-poppins" onClick={(e) => e.stopPropagation()}>
          {row.isBlocked && row.currentStage === 'SAP_PR' && onRetrySapSync && (
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs text-rose-600 hover:bg-rose-50 border-rose-200 font-bold rounded-lg cursor-pointer"
              onClick={() => onRetrySapSync(row.id)}
            >
              <RefreshCw className="h-3 w-3 mr-1" /> Retry SAP
            </Button>
          )}

          <Button
            size="sm"
            className="h-7 px-3 text-xs font-bold rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground font-poppins cursor-pointer"
            onClick={() => navigate(ROUTES.PIPELINE.DETAILS(row.id))}
          >
            <Eye className="h-3.5 w-3.5 mr-1" /> Workspace
          </Button>
        </div>
      ),
    },
  ];

  return (
    <DataTable
      data={records}
      columns={columns}
      isLoading={isLoading}
      hideSearch={true}
      fixedHeight={true}
      containerHeightClass="max-h-[580px] overflow-y-auto overflow-x-hidden"
      emptyTitle="No Pipeline Transactions Found"
      emptyDescription="No procurement requisitions match your active filter criteria."
      onClearFilters={onClearFilters}
      onRowClick={(row) => navigate(ROUTES.PIPELINE.DETAILS(row.id))}
    />
  );
};
